import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { ASSIGN_TECHNICIAN_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  AssignTechnicianPayload,
  AssignTechnicianResponseData,
  AssignTechnicianResult,
} from '../types/assignTechnicianType';
import serviceRequestEndpoints from './serviceRequestEndpoints';

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return ASSIGN_TECHNICIAN_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (typeof obj.error === 'string' && obj.error.trim().length > 0) {
      // Map specific 409 strings to localized copy.
      if (status === 409 && /status changed/i.test(obj.error)) {
        return ASSIGN_TECHNICIAN_ERROR_MESSAGES.STATUS_CHANGED;
      }
      if (status === 409 && /unavailable|conflicting/i.test(obj.error)) {
        return ASSIGN_TECHNICIAN_ERROR_MESSAGES.TECHNICIAN_CONFLICT;
      }
      return obj.error;
    }
    if (typeof obj.message === 'string' && obj.message.trim().length > 0) {
      return obj.message;
    }
  }
  return ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const assignTechnician = async ({
  serviceRequestId,
  technicianId,
  startDate,
  endDate,
  startTime,
  endTime,
  adminNotes,
  formId,
}: AssignTechnicianPayload): Promise<AssignTechnicianResult> => {
  const trimmedId = serviceRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  const trimmedNotes = typeof adminNotes === 'string' ? adminNotes.trim() : '';

  const body: Record<string, unknown> = {
    technician_id: technicianId,
    start_date: startDate,
    end_date: endDate,
  };
  if (startTime) {
    body['start_time'] = startTime;
  }
  if (endTime) {
    body['end_time'] = endTime;
  }
  if (trimmedNotes.length > 0) {
    body['admin_notes'] = trimmedNotes;
  }
  if (formId) {
    body['form_id'] = formId;
  }

  try {
    const result = await apiService.postAPI(
      serviceRequestEndpoints.ASSIGN_TECHNICIAN(trimmedId),
      body
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        data?: AssignTechnicianResponseData;
      };
      return {
        success: true,
        ...(payload.data ? { data: payload.data } : {}),
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error: ASSIGN_TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
